import { useState, useEffect, useCallback } from "react"
import { useAgency } from "../hooks/useAgency"
import { Link, useNavigate } from "react-router-dom"

export default function AgencyDashboard() {
    const navigate = useNavigate()
    const {
        agency, workspaces, insights, loading,
        fetchAgency, createAgency, createWorkspace, archiveWorkspace,
        switchWorkspace, fetchInsights,
    } = useAgency()

    const [tab, setTab] = useState("workspaces")  // workspaces | insights | members
    const [showNewWsModal, setShowNewWsModal] = useState(false)
    const [showSetupModal, setShowSetupModal] = useState(false)
    const [search, setSearch] = useState("")

    const load = useCallback(async () => {
        const data = await fetchAgency()
        if (!data) {
            // Not an agency user — show setup modal
            setShowSetupModal(true)
        }
        if (tab === "insights") fetchInsights()
    }, [fetchAgency, fetchInsights, tab])

    useEffect(() => { load() }, [load])

    const filteredWorkspaces = (workspaces || []).filter(ws =>
        ws.name.toLowerCase().includes(search.toLowerCase()) ||
        ws.clientInfo?.company?.toLowerCase().includes(search.toLowerCase())
    )

    const handleEnterWorkspace = async (ws) => {
        try {
            await switchWorkspace(ws._id)
            navigate("/dashboard")
        } catch (e) {
            alert(e.message)
        }
    }

    const handleArchive = async (ws) => {
        if (!window.confirm(`Archive workspace "${ws.name}"? Data will be preserved for 30 days.`)) return
        try {
            await archiveWorkspace(ws._id, "manual_archive")
        } catch (e) {
            alert(e.message)
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-gray-400 text-sm">Loading agency dashboard…</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                            {agency?.name || "Agency Dashboard"}
                        </h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {workspaces.length} active workspace{workspaces.length !== 1 ? "s" : ""} · Agency plan
                        </p>
                    </div>
                    <button
                        onClick={() => setShowNewWsModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition"
                    >
                        <span className="text-lg leading-none">+</span>
                        Add client
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6">
                {/* Tabs */}
                <div className="flex gap-1 mb-6 border-b dark:border-gray-700">
                    {["workspaces", "insights", "members"].map(t => (
                        <button
                            key={t}
                            onClick={() => { setTab(t); if (t === "insights") fetchInsights() }}
                            className={`px-4 py-2.5 text-sm font-medium capitalize border-b-2 transition -mb-px ${
                                tab === t
                                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400"
                                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                            }`}
                        >
                            {t}
                        </button>
                    ))}
                </div>

                {/* Workspaces tab */}
                {tab === "workspaces" && (
                    <div className="space-y-4">
                        {/* Search + filter */}
                        <div className="flex gap-3 items-center">
                            <input
                                type="text"
                                placeholder="Search workspaces…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="flex-1 max-w-xs text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <p className="text-sm text-gray-400">{filteredWorkspaces.length} result{filteredWorkspaces.length !== 1 ? "s" : ""}</p>
                        </div>

                        {/* Workspace cards */}
                        {filteredWorkspaces.length === 0 ? (
                            <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                                <p className="text-base mb-2">No workspaces yet</p>
                                <button onClick={() => setShowNewWsModal(true)} className="text-indigo-600 dark:text-indigo-400 text-sm underline">
                                    Add your first client
                                </button>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-700/50 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                            <th className="text-left px-4 py-3">Workspace</th>
                                            <th className="text-right px-4 py-3">Forms</th>
                                            <th className="text-right px-4 py-3">Responses/mo</th>
                                            <th className="text-right px-4 py-3">MRR</th>
                                            <th className="text-right px-4 py-3">Last activity</th>
                                            <th className="px-4 py-3" />
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y dark:divide-gray-700">
                                        {filteredWorkspaces.map(ws => (
                                            <WorkspaceRow
                                                key={ws._id}
                                                workspace={ws}
                                                onEnter={() => handleEnterWorkspace(ws)}
                                                onArchive={() => handleArchive(ws)}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Workspace limit indicator */}
                        {agency && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
                                {workspaces.length} / {(agency.maxWorkspaces || 10) + (agency.additionalWorkspaces || 0)} workspaces used
                                {workspaces.length >= (agency.maxWorkspaces || 10) && (
                                    <span className="ml-2 text-amber-500 font-medium">· Purchase add-on to create more</span>
                                )}
                            </p>
                        )}
                    </div>
                )}

                {/* Insights tab */}
                {tab === "insights" && <InsightsTab insights={insights} />}

                {/* Members tab — link to members management page */}
                {tab === "members" && <MembersTab />}
            </div>

            {/* New workspace modal */}
            {showNewWsModal && (
                <NewWorkspaceModal
                    onClose={() => setShowNewWsModal(false)}
                    onCreate={async ({ name, clientInfo }) => {
                        await createWorkspace({ name, clientInfo })
                        setShowNewWsModal(false)
                    }}
                />
            )}

            {/* Agency setup modal for non-agency users who landed here */}
            {showSetupModal && (
                <AgencySetupModal
                    onClose={() => navigate("/dashboard")}
                    onCreate={async ({ name }) => {
                        await createAgency({ name })
                        setShowSetupModal(false)
                        await load()
                    }}
                />
            )}
        </div>
    )
}

// ── Workspace row ─────────────────────────────────────────────────────────────

function WorkspaceRow({ workspace: ws, onEnter, onArchive }) {
    const [menuOpen, setMenuOpen] = useState(false)
    const metrics = ws.metrics || {}
    const mrr = ws.billing?.isBillable ? (ws.billing?.monthlyFee || 0) : null
    const currency = ws.billing?.currency || "USD"

    return (
        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
            <td className="px-4 py-3">
                <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{ws.name}</span>
                    {ws.clientInfo?.company && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{ws.clientInfo.company}</p>
                    )}
                </div>
            </td>
            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                {metrics.formCount ?? "—"}
            </td>
            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                {metrics.responseCount ?? "—"}
            </td>
            <td className="px-4 py-3 text-right text-sm">
                {mrr !== null ? (
                    <span className="font-medium text-green-600 dark:text-green-400">
                        {currency === "INR" ? "₹" : "$"}{mrr.toLocaleString()}/mo
                    </span>
                ) : (
                    <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
            </td>
            <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                {ws.lastActivityAt ? new Date(ws.lastActivityAt).toLocaleDateString() : "Never"}
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                    <button
                        onClick={onEnter}
                        className="text-xs px-2.5 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                        Enter
                    </button>
                    <Link
                        to={`/agency/workspaces/${ws._id}/branding`}
                        onClick={() => sessionStorage.setItem(`ws_name_${ws._id}`, ws.name)}
                        className="text-xs px-2.5 py-1 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                        Branding
                    </Link>
                    <div className="relative">
                        <button
                            onClick={() => setMenuOpen(o => !o)}
                            className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                            </svg>
                        </button>
                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg z-10 py-1">
                                <Link
                                    to={`/agency/workspace/${ws._id}/settings`}
                                    className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    Settings
                                </Link>
                                <Link
                                    to={`/agency/workspaces/${ws._id}/branding`}
                                    className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    onClick={() => { setMenuOpen(false); sessionStorage.setItem(`ws_name_${ws._id}`, ws.name) }}
                                >
                                    White-label branding
                                </Link>
                                <button
                                    onClick={() => { setMenuOpen(false); onArchive() }}
                                    className="block w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Archive
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    )
}

// ── Insights tab ──────────────────────────────────────────────────────────────

function InsightsTab({ insights }) {
    if (!insights) return <p className="text-sm text-gray-400">Loading insights…</p>

    const { summary, perWorkspace } = insights
    return (
        <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: "Active workspaces", value: summary.activeWorkspaces },
                    { label: "Forms (total)", value: summary.totalForms },
                    { label: "Responses this month", value: summary.responsesThisMonth },
                    { label: "Total MRR (reseller)", value: summary.billableTotal ? `$${summary.billableTotal}/mo` : "—" },
                ].map(card => (
                    <div key={card.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{card.label}</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white font-display">{card.value}</p>
                    </div>
                ))}
            </div>

            {/* Per-workspace breakdown */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 border-b dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Per-client breakdown</h3>
                </div>
                <table className="w-full">
                    <thead>
                        <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-700/50">
                            <th className="text-left px-4 py-2">Workspace</th>
                            <th className="text-right px-4 py-2">Forms</th>
                            <th className="text-right px-4 py-2">Total responses</th>
                            <th className="text-right px-4 py-2">This month</th>
                            <th className="text-right px-4 py-2">Monthly fee</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                        {(perWorkspace || []).map(ws => (
                            <tr key={ws.workspaceId} className="text-sm">
                                <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{ws.name}</td>
                                <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{ws.forms}</td>
                                <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{ws.responses}</td>
                                <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{ws.responsesThisMonth}</td>
                                <td className="px-4 py-2.5 text-right">
                                    {ws.billing?.isBillable
                                        ? <span className="font-medium text-green-600 dark:text-green-400">${ws.billing.monthlyFee}/mo</span>
                                        : <span className="text-gray-300 dark:text-gray-600">—</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ── Members tab (stub) ────────────────────────────────────────────────────────

function MembersTab() {
    return (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
            <p className="text-base mb-2">Member management</p>
            <Link to="/agency/members" className="text-indigo-600 dark:text-indigo-400 text-sm underline">
                Go to full member management →
            </Link>
        </div>
    )
}

// ── New workspace modal ───────────────────────────────────────────────────────

function NewWorkspaceModal({ onClose, onCreate }) {
    const [name, setName] = useState("")
    const [clientName, setClientName] = useState("")
    const [clientEmail, setClientEmail] = useState("")
    const [company, setCompany] = useState("")
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState(null)

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!name.trim()) { setErr("Workspace name is required"); return }
        setSaving(true)
        setErr(null)
        try {
            await onCreate({
                name,
                clientInfo: { name: clientName, email: clientEmail, company },
            })
        } catch (e) {
            setErr(e.message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Add client workspace</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Workspace name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Acme Corp"
                            className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Client name</label>
                            <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Jane Smith"
                                className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Company</label>
                            <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Corp"
                                className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Client email</label>
                        <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="jane@acme.com"
                            className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    </div>
                    {err && <p className="text-xs text-red-500">{err}</p>}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving} className="flex-1 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                            {saving ? "Creating…" : "Create workspace"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

// ── Agency setup modal (first-time) ──────────────────────────────────────────

function AgencySetupModal({ onClose, onCreate }) {
    const [name, setName] = useState("")
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState(null)

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!name.trim()) { setErr("Agency name is required"); return }
        setSaving(true)
        setErr(null)
        try {
            await onCreate({ name })
        } catch (e) {
            setErr(e.message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Set up your agency</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                    Give your agency a name to get started. You can change this later.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="e.g. Bright Digital Agency"
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        autoFocus
                    />
                    {err && <p className="text-xs text-red-500">{err}</p>}
                    {err?.includes("plan") && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                            The Agency dashboard requires an Agency plan subscription.{" "}
                            <a href="/pricing" className="underline">Upgrade now →</a>
                        </p>
                    )}
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="flex-1 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                            Go back
                        </button>
                        <button type="submit" disabled={saving} className="flex-1 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                            {saving ? "Setting up…" : "Create agency"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
